import React, { useMemo } from "react";
import {
  Edit,
  SimpleForm,
  TextInput,
  ReferenceInput,
  DateInput,
  RadioButtonGroupInput,
  required,
  useRecordContext,
  type EditProps,
  type RaRecord,
} from "react-admin";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import { ITSInput } from "./common/FmbReceiptItsInput";
import { LINE_KIND } from "./allocation/line-kind";
import type { AllocationFormRow } from "./allocation/types";
import { PaymentTotalAndAllocationsSection } from "./allocation/PaymentTotalAndAllocationsSection";
import {
  renderFmbReceiptItsOption,
  transformFmbReceiptPayload,
  validateFmbReceiptForm,
} from "./fmb-receipt-create-form";

/**
 * Map an existing DB allocation row to the form's AllocationFormRow shape.
 * The DB shape uses `fmbContributionId` / `fmbTakhmeenId` as the discriminator;
 * the form uses an explicit `lineKind`.
 */
function dbAllocationToFormRow(a: {
  fmbContributionId?: string | null;
  fmbTakhmeenId?: string | null;
  amount?: number | null;
}): AllocationFormRow {
  const isContribution = Boolean(a?.fmbContributionId);
  return {
    lineKind: isContribution ? LINE_KIND.CONTRIBUTION : LINE_KIND.ANNUAL,
    fmbTakhmeenId: isContribution ? null : (a?.fmbTakhmeenId ?? null),
    fmbContributionId: isContribution ? (a?.fmbContributionId ?? null) : null,
    amount: a?.amount != null ? a.amount : "",
  };
}

/**
 * Normalize the payment mode loaded from the DB into one of CASH / ONLINE / CHEQUE so the
 * RadioButtonGroupInput has a valid initial selection. Imported receipts may carry legacy
 * values like `online_transfer`, `cash`, `cheque`, `payment_gateway`; map those onto the
 * canonical 3 the form supports.
 */
function normalizePaymentMode(mode: unknown): string {
  if (typeof mode !== "string") return "CASH";
  const upper = mode.trim().toUpperCase();
  if (upper === "CASH") return "CASH";
  if (upper === "CHEQUE") return "CHEQUE";
  if (upper === "ONLINE") return "ONLINE";
  if (upper === "ONLINE_TRANSFER" || upper === "PAYMENT_GATEWAY") return "ONLINE";
  // Anything we don't recognise — default to ONLINE since most legacy values are bank transfers.
  return "ONLINE";
}

function FmbReceiptEditForm() {
  const record = useRecordContext<
    RaRecord & {
      fmbId?: string | null;
      amount?: number | null;
      creditUsed?: number | null;
      paymentMode?: string | null;
      remarks?: string | null;
      transactionId?: string | null;
      receiptDate?: string | Date | null;
      receiptNo?: string | null;
      allocations?: Array<{
        fmbContributionId?: string | null;
        fmbTakhmeenId?: string | null;
        amount?: number | null;
      }>;
    }
  >();

  const recordAllocations = record?.allocations;
  const existingFormAllocations: AllocationFormRow[] = useMemo(() => {
    const allocs = Array.isArray(recordAllocations) ? recordAllocations : [];
    return allocs.map(dbAllocationToFormRow);
  }, [recordAllocations]);

  const allocationTemplate: AllocationFormRow[] = useMemo(() => {
    if (existingFormAllocations.length > 0) return existingFormAllocations.slice(0, 1);
    return [
      {
        lineKind: LINE_KIND.ANNUAL,
        fmbTakhmeenId: null,
        fmbContributionId: null,
        amount: "",
      },
    ];
  }, [existingFormAllocations]);

  const defaultValues = useMemo(
    () => ({
      fmbId: record?.fmbId ?? undefined,
      paymentMode: normalizePaymentMode(record?.paymentMode),
      receiptDate: record?.receiptDate ? new Date(record.receiptDate) : undefined,
      amount: record?.amount ?? "",
      creditUsed: record?.creditUsed ?? 0,
      remarks: record?.remarks ?? "",
      transactionId:
        record?.transactionId ?? (record as { paymentRef?: string | null })?.paymentRef ?? "",
      amountConfirmed: true,
      allocations: existingFormAllocations,
    }),
    [record, existingFormAllocations]
  );

  return (
    <SimpleForm
      warnWhenUnsavedChanges
      sx={{ maxWidth: 760 }}
      defaultValues={defaultValues}
      validate={validateFmbReceiptForm}
    >
      <Box sx={{ mb: 2 }}>
        <Alert severity="info" variant="outlined">
          {`Allocate this receipt across any of the FMB's takhmeen periods or contributions. ` +
            `Lines for fully-paid past periods are shown so historical receipts can be allocated ` +
            `retroactively — pending balance is informational only and doesn't restrict allocation.`}
        </Alert>
      </Box>

      <Grid container spacing={1}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextInput source="receiptNo" label="Receipt no." fullWidth disabled />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <ReferenceInput source="fmbId" reference="fmbData">
            <ITSInput
              fullWidth
              size="small"
              label="ITS No."
              optionText={renderFmbReceiptItsOption}
              shouldRenderSuggestions={(val: string) => val.trim().length > 3}
              noOptionsText="Enter valid ITS No."
              syncAnnualContext
            />
          </ReferenceInput>
        </Grid>
      </Grid>

      <PaymentTotalAndAllocationsSection allocationTemplate={allocationTemplate} editMode />

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <DateInput source="receiptDate" label="Receipt date" fullWidth />
        </Grid>
        <Grid size={{ xs: 12, sm: 12 }}>
          <RadioButtonGroupInput
            source="paymentMode"
            label="Payment mode"
            choices={[
              { id: "CASH", name: "CASH" },
              { id: "ONLINE", name: "ONLINE" },
              { id: "CHEQUE", name: "CHEQUE" },
            ]}
            fullWidth
            validate={[required()]}
          />
        </Grid>
      </Grid>

      <TextInput source="transactionId" label="Transaction ID / Reference" fullWidth />
      <TextInput source="remarks" fullWidth multiline />
    </SimpleForm>
  );
}

export default function FmbReceiptEdit(props: EditProps) {
  return (
    <Edit
      {...props}
      mutationMode="pessimistic"
      transform={transformFmbReceiptPayload}
      redirect="show"
    >
      <FmbReceiptEditForm />
    </Edit>
  );
}
